import { FC } from "react";
import styled from "styled-components";

import { OptimisationTestUnitResource } from "@/types/resources";
import { OptimisationTestStatusEnum } from "@/utils/enums";
import Text from "@/components/Text";
import { Color } from "@/utils/theme";

const Holder = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  display: inline-flex;
  height: 38px;
  align-items: center;
  gap: 16px;
  padding: 0 16px;
  margin-right: 16px;
`;

const Badge = styled(Text)<{ $backgroundColor: Color }>`
  margin-left: 5px;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ $backgroundColor, theme }) =>
    theme.colors[$backgroundColor]};
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Inner = styled.div`
  display: inline-flex;
  align-items: center;
`;

interface Props {
  tests: OptimisationTestUnitResource[];
}

const TestsStatusHolder: FC<Props> = ({ tests }) => {
  const processingCount = tests.filter(
    (test) => test.status === OptimisationTestStatusEnum.PROCESSING
  ).length;

  const waitingForDataCount = tests.filter(
    (test) => test.status === OptimisationTestStatusEnum.WAITING_FOR_DATA
  ).length;

  const errorsCount = tests.filter((test) => test.hasError).length;

  if (!processingCount && !waitingForDataCount && !errorsCount) return null;

  return (
    <Holder>
      {!!errorsCount && (
        <Inner>
          <Text as="span" $type="smallBold" $color="gray">
            Error:
          </Text>
          <Badge
            as="span"
            $color="white"
            $type="extraSmall"
            $backgroundColor="error"
          >
            {errorsCount}
          </Badge>
        </Inner>
      )}
      {!!processingCount && (
        <Inner>
          <Text as="span" $type="smallBold" $color="gray">
            Preparing:
          </Text>
          <Badge
            as="span"
            $color="white"
            $type="extraSmall"
            $backgroundColor="warning"
          >
            {processingCount}
          </Badge>
        </Inner>
      )}
      {!!waitingForDataCount && (
        <Inner>
          <Text as="span" $type="smallBold" $color="gray">
            Waiting for data:
          </Text>
          <Badge
            as="span"
            $color="white"
            $type="extraSmall"
            $backgroundColor="info"
          >
            {waitingForDataCount}
          </Badge>
        </Inner>
      )}
    </Holder>
  );
};

export default TestsStatusHolder;
